/**
 * @file useProductKinds.ts
 * @description Загрузка справочника «Вид товара» (phase19.22, W22).
 *
 * Архивные виды грузим всегда: они продолжают действовать у товаров и
 * категорий, и без них «Как в категории — …» и бейдж в дереве показали бы
 * «не задан». Отбор «только активные» — в опциях выбора (`kindSelectOptions`).
 *
 * Список после мутаций перечитывается (`refresh`), а не правится руками —
 * счётчики товаров считает сервер.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import { ApiError } from "../../../lib/api-client";
import { productKindsApi, type ProductKind } from "../../../lib/api-product-kinds";

export interface ProductKindsState {
  kinds: ProductKind[];
  kindsById: Map<string, ProductKind>;
  /** Товаров без действующего вида — баннер «Проставить по категориям». */
  productsWithoutKind: number;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useProductKinds(): ProductKindsState {
  const [kinds, setKinds] = useState<ProductKind[]>([]);
  const [productsWithoutKind, setProductsWithoutKind] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await productKindsApi.list({ includeArchived: true });
      setKinds(res.items);
      setProductsWithoutKind(res.productsWithoutKind);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить виды товара");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const kindsById = useMemo(() => new Map(kinds.map((k) => [k.id, k])), [kinds]);

  return { kinds, kindsById, productsWithoutKind, loading, error, refresh };
}
